import { useLocation } from "react-router-dom";
import { ConditionalLink } from "components/conditionalLink";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { ActionItemCard } from "components/HomeActionItems/ActionItemCard";
import NoPendingActionItems from "static/images/NoPendingActionItems.svg";
import {
  useGetPendingActionItemsQuery,
  type PendingActionItem,
} from "store/apis/actionItems";

type PendingActionItemRowProps = {
  item: PendingActionItem;
  filingName: string;
};

const PendingActionItemRow = ({
  item,
  filingName,
}: PendingActionItemRowProps) => {
  const { search } = useLocation();

  return (
    <div className="t-space-y-3 t-rounded-lg t-bg-surface-lighter-grey t-p-3">
      <div className="t-flex t-items-center t-justify-between t-gap-3">
        <div className="t-flex t-items-center t-gap-1.5">
          <div className="t-text-subtext-sm t-text-text-100">
            {item.sender.name}
          </div>
          <div className="t-text-caption t-text-neutral-20">|</div>
          <div className="t-text-body-sm t-text-text-30">{filingName}</div>
        </div>
        <ConditionalLink
          to={`/chat/${item.channel_id}/${item.message_id}${search}`}
          target="_blank"
        >
          <Button customType="link" size="small">
            Go to chat
          </Button>
        </ConditionalLink>
      </div>
      <ActionItemCard
        type={item.custom_type}
        customData={item.custom_data}
        messageId={item.message_id}
        channelId={item.channel_id}
        card_status={item.card_status}
      />
    </div>
  );
};

type FilingPendingItemsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskUuid: string;
  filingName: string;
};

export const FilingPendingItemsModal = ({
  open,
  onOpenChange,
  taskUuid,
  filingName,
}: FilingPendingItemsModalProps) => {
  const { data, isLoading, isSuccess, isError } = useGetPendingActionItemsQuery(
    { taskUuid },
    { skip: !open }
  );

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange}>
      <Modal.Content size="large" useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Pending items</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isError={isError}
            isEmpty={(data?.items.length ?? 0) === 0}
          >
            <Async.Empty>
              <div className="t-flex t-h-80 t-w-full t-flex-col t-items-center t-justify-center t-gap-8">
                <img src={NoPendingActionItems} alt="NoPendingActionItems" />
                <div className="t-text-subtitle t-text-text-30">
                  Awesome! No pending items
                </div>
              </div>
            </Async.Empty>
            <Async.ErrorHandler>
              <div className="t-flex t-h-80 t-w-full t-flex-col t-items-center t-justify-center">
                <div className="t-text-subtitle t-text-text-30">
                  Couldn&apos;t load pending items
                </div>
              </div>
            </Async.ErrorHandler>
            <Async.Success>
              <div className="t-space-y-5">
                {data?.items.map((item) => (
                  <PendingActionItemRow
                    key={item.message_id}
                    item={item}
                    filingName={filingName}
                  />
                ))}
              </div>
            </Async.Success>
          </Async.Root>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
